import React, { useState } from 'react';
import { AdminRoute } from './AdminShell';
import { LayoutDashboardIcon, UsersIcon, GraduationCapIcon, SchoolIcon, HeartIcon, UserCogIcon, BookOpenIcon, BarChart2Icon, ShieldCheckIcon, WrenchIcon, ActivityIcon, TerminalIcon, SettingsIcon, ChevronRightIcon, HomeIcon } from 'lucide-react';
interface NavItem {
  id: AdminRoute;
  label: string;
  icon: React.ReactNode;
  description?: string;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}
const NAV_GROUPS: NavGroup[] = [{
  label: 'Overzicht',
  items: [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboardIcon className="w-4 h-4" data-id="element-56" />,
    description: 'Systeem overzicht'
  }]
}, {
  label: 'Gebruikers',
  items: [{
    id: 'learners',
    label: 'Leerlingen',
    icon: <UsersIcon className="w-4 h-4" data-id="element-57" />,
    description: 'Leerling accounts'
  }, {
    id: 'teachers',
    label: 'Docenten',
    icon: <GraduationCapIcon className="w-4 h-4" data-id="element-58" />,
    description: 'Docent accounts'
  }, {
    id: 'schools',
    label: 'Scholen',
    icon: <SchoolIcon className="w-4 h-4" data-id="element-59" />,
    description: 'School overzicht'
  }, {
    id: 'parents',
    label: 'Ouders',
    icon: <HeartIcon className="w-4 h-4" data-id="element-60" />,
    description: 'Ouder accounts'
  }, {
    id: 'user-management',
    label: 'Gebruikersbeheer',
    icon: <UserCogIcon className="w-4 h-4" data-id="element-61" />,
    description: 'Alle accounts beheren'
  }]
}, {
  label: 'Inhoud',
  items: [{
    id: 'question-bank',
    label: 'Vragenbank',
    icon: <BookOpenIcon className="w-4 h-4" data-id="element-62" />,
    description: 'Alle vragen & seeds'
  }, {
    id: 'quality-stats',
    label: 'Kwaliteitsstatistieken',
    icon: <BarChart2Icon className="w-4 h-4" data-id="element-63" />,
    description: 'Hoe goed is de inhoud?'
  }, {
    id: 'validation-rules',
    label: 'Validatieregels',
    icon: <ShieldCheckIcon className="w-4 h-4" data-id="element-64" />,
    description: 'Compliance checks'
  }, {
    id: 'support-content',
    label: 'Ondersteuning',
    icon: <BookOpenIcon className="w-4 h-4" data-id="element-65" />,
    description: 'Uitleg & hints'
  }]
}, {
  label: 'Systeem',
  items: [{
    id: 'developer-tools',
    label: 'Developer Tools',
    icon: <WrenchIcon className="w-4 h-4" data-id="element-66" />,
    description: 'Seeds & taxonomie'
  }, {
    id: 'logs',
    label: 'Activiteitenlog',
    icon: <ActivityIcon className="w-4 h-4" data-id="element-67" />,
    description: 'Sessie logs'
  }, {
    id: 'console',
    label: 'Console',
    icon: <TerminalIcon className="w-4 h-4" data-id="element-68" />,
    description: 'Diagnostics'
  }]
}, {
  label: 'Beheer',
  items: [{
    id: 'settings',
    label: 'Instellingen',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-69" />,
    description: 'Systeem instellingen'
  }]
}];
interface AdminSidebarProps {
  activeRoute: AdminRoute;
  onNavigate: (route: AdminRoute) => void;
  onReturnHome?: () => void;
}
export function AdminSidebar({
  activeRoute,
  onNavigate,
  onReturnHome
}: AdminSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  return <aside className={`flex flex-col bg-white border-r border-gray-200 transition-all duration-200 flex-shrink-0 ${collapsed ? 'w-14' : 'w-56'}`} data-id="element-70">
      {/* Header */}
      <div className={`flex items-center border-b border-gray-100 flex-shrink-0 ${collapsed ? 'justify-center px-2 py-4' : 'justify-between px-4 py-4'}`} data-id="element-71">
        {!collapsed && <div data-id="element-72">
            <div className="text-sm font-bold text-gray-900" data-id="element-73">Admin</div>
            <div className="text-xs text-gray-400" data-id="element-74">Beheercentrum</div>
          </div>}
        <button onClick={() => setCollapsed(c => !c)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" aria-label={collapsed ? 'Uitklappen' : 'Inklappen'} data-id="element-75">
          <ChevronRightIcon className={`w-4 h-4 transition-transform ${collapsed ? '' : 'rotate-180'}`} data-id="element-76" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2" data-id="element-77">
        {NAV_GROUPS.map(group => <div key={group.label} className="mb-4" data-id="element-78">
            {!collapsed && <div className="px-2 mb-1" data-id="element-79">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-80">
                  {group.label}
                </span>
              </div>}
            <div className="space-y-0.5" data-id="element-81">
              {group.items.map(item => {
            const isActive = activeRoute === item.id;
            return <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : undefined} className={`w-full flex items-center gap-2.5 rounded-lg transition-colors ${collapsed ? 'justify-center px-2 py-2.5' : 'px-2.5 py-2'} ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-82">
                    <span className={`flex-shrink-0 ${isActive ? 'text-blue-600' : ''}`} data-id="element-83">
                      {item.icon}
                    </span>
                    {!collapsed && <span className={`text-sm truncate ${isActive ? 'font-medium' : ''}`} data-id="element-84">
                        {item.label}
                      </span>}
                    {!collapsed && isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" data-id="element-85" />}
                  </button>;
          })}
            </div>
          </div>)}
      </nav>

      {/* Footer */}
      <div className={`flex-shrink-0 border-t border-gray-100 ${collapsed ? 'p-2' : 'p-4'}`} data-id="element-86">
        {onReturnHome && <button onClick={onReturnHome} className={`w-full flex items-center justify-center gap-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors ${collapsed ? 'p-2' : 'px-3 py-2 text-sm font-medium'}`} title={collapsed ? 'Terug naar Home' : undefined} data-id="element-87">
            <LayoutDashboardIcon className="w-4 h-4" data-id="element-88" />
            {!collapsed && 'Terug naar Home'}
          </button>}
        {!collapsed && <div className="text-xs text-gray-400 text-center mt-3 font-medium" data-id="element-89">
            Meester djay Admin v2
          </div>}
      </div>
    </aside>;
}